import { useIntelligenceItems } from '@/hooks';
import { log, spin } from '@/utils';
import {
  Add as AddIcon,
  Autorenew as AutorenewIcon,
  Done as DoneIcon,
} from '@mui/icons-material';
import {
  alpha,
  Avatar,
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useParams } from 'react-router';
import { IntelligenceItem } from './IntelligenceItem';
import { IntelligenceItemEditor } from './IntelligenceItemEditor';

export function PriorityIntelligence({ groupCodes, onComplete }) {
  const {
    query: { data: items, isFetching, isLoading, refetch },
  } = useIntelligenceItems(groupCodes);
  const [editCode, setEditCode] = useState(null);
  const { code } = useParams();

  log('View', 'Priority Intelligence', { code, groupCodes });

  const handleSettingsClick = (code) => () => {
    setEditCode(code);
  };

  function handleRefreshClick() {
    refetch();
  }

  function handleAddClick() {
    setEditCode('new');
  }

  function onEditorClose() {
    setEditCode(null);
  }

  function handleCompleteClick() {
    onComplete();
  }

  return (
    <Box sx={{ px: 1 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={(theme) => ({
          alignItems: 'center',
          position: 'sticky',
          top: 48,
          backgroundColor: alpha(theme.palette.background.paper, 0.8),
          zIndex: 700,
          pl: 1,
          py: 1,
        })}
      >
        <Typography variant="h6">Priority Intelligence</Typography>
        <Box>
          <Tooltip title="Done" placement="right">
            <IconButton onClick={handleCompleteClick}>
              <DoneIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Box>
          <Tooltip title="Refresh" placement="bottom">
            <Box component="span">
              <IconButton
                onClick={handleRefreshClick}
                disabled={isFetching || isLoading}
              >
                <AutorenewIcon
                  fontSize="inherit"
                  sx={
                    isLoading || isFetching
                      ? { animation: `${spin} 2s linear infinite` }
                      : undefined
                  }
                />
              </IconButton>
            </Box>
          </Tooltip>
          <Tooltip title="Add" placement="left">
            <IconButton size="small" onClick={handleAddClick}>
              <Avatar
                sx={{
                  color: 'secondary.contrastText',
                  bgcolor: 'secondary.main',
                  width: 32,
                  height: 32,
                  fontSize: 24,
                }}
              >
                <AddIcon fontSize="inherit" />
              </Avatar>
            </IconButton>
          </Tooltip>
        </Box>
      </Stack>
      {!isLoading && !isFetching && items.length === 0 && (
        <Typography variant="body2" color="textSecondary" sx={{ px: 1 }}>
          No items
        </Typography>
      )}
      <Stack spacing={1} sx={{ pb: 1 }}>
        {items.map((item) => (
          <IntelligenceItem
            key={item.code}
            item={item}
            onSettingsClick={handleSettingsClick(item.code)}
          />
        ))}
      </Stack>
      <IntelligenceItemEditor
        code={editCode}
        isOpen={Boolean(editCode)}
        onClose={onEditorClose}
        groupCodes={groupCodes}
      />
    </Box>
  );
}
